// Default palette matches the original hardcoded Chakra tokens used across the menu pages.
const DEFAULT_THEME = {
  appBg: "gray.50",
  cardBg: "white",
  cardHover: "gray.50",
  primaryLight: "teal.50",
  primary: "teal.500",
  border: "gray.200",
  textMuted: "gray.500",
  textPrimary: "gray.800",
  textSecondary: "gray.600",
  onPrimary: "white",
};

const withHash = (value) => {
  if (!value || typeof value !== "string") return null;
  return value.startsWith("#") ? value : `#${value}`;
};

const getLuminance = (hex) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.substring(0, 2), 16) / 255;
  const g = parseInt(value.substring(2, 4), 16) / 255;
  const b = parseInt(value.substring(4, 6), 16) / 255;
  const channel = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

// Builds a theme object from a restaurant's colorScheme. Falls back to the
// original default palette when the scheme (or its properties) is missing/empty.
export const getMenuTheme = (colorScheme) => {
  const props = colorScheme?.properties;
  if (!props || Object.keys(props).length === 0) {
    return DEFAULT_THEME;
  }

  const primary = withHash(props.primary) || DEFAULT_THEME.primary;
  const textPrimary = withHash(props.textPrimary) || DEFAULT_THEME.textPrimary;

  return {
    appBg: withHash(props.appBg) || DEFAULT_THEME.appBg,
    cardBg: withHash(props.cardBg) || DEFAULT_THEME.cardBg,
    cardHover: withHash(props.cardHover) || DEFAULT_THEME.cardHover,
    primaryLight: withHash(props.primaryLight) || DEFAULT_THEME.primaryLight,
    primary,
    border: withHash(props.border) || DEFAULT_THEME.border,
    textMuted: withHash(props.textMuted) || DEFAULT_THEME.textMuted,
    textPrimary,
    textSecondary: withHash(props.textSecondary) || DEFAULT_THEME.textSecondary,
    onPrimary: getLuminance(primary) > 0.55 ? textPrimary : "white",
  };
};

// Chakra `sx` styles for restaurant-supplied HTML (e.g. specialNotes),
// giving plain <h2>/<h3>/<h4>/<p>/<ul> markup a readable visual hierarchy.
export const getRichTextSx = (theme) => ({
  "& h2": {
    fontSize: "lg",
    fontWeight: "bold",
    color: theme.textPrimary,
    marginBottom: "0.75rem",
    paddingBottom: "0.5rem",
    borderBottom: `1px solid ${theme.border}`,
  },
  "& h3": {
    fontSize: "sm",
    fontWeight: "bold",
    color: theme.primary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginTop: "1.5rem",
    marginBottom: "0.5rem",
  },
  "& h3:first-of-type": {
    marginTop: 0,
  },
  "& h4": {
    fontSize: "sm",
    fontWeight: "semibold",
    color: theme.textPrimary,
    marginTop: "1rem",
    marginBottom: "0.25rem",
  },
  "& p": {
    fontSize: "sm",
    color: theme.textSecondary,
    lineHeight: 1.6,
    marginBottom: "0.75rem",
  },
  "& p:last-child": {
    marginBottom: 0,
  },
  "& ul, & ol": {
    paddingLeft: "1.25rem",
    marginBottom: "0.75rem",
  },
  "& li": {
    fontSize: "sm",
    color: theme.textSecondary,
    lineHeight: 1.6,
    marginBottom: "0.25rem",
  },
  "& strong, & b": {
    color: theme.textPrimary,
  },
  "& a": {
    color: theme.primary,
    textDecoration: "underline",
  },
});

export default getMenuTheme;
